import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {apiUrl} from "../../../../../api/baseURL";
import IconButton from "../../../../../utilities/Buttons/IconButton";
import {COLOR_SCHEME} from "../../../../../utilities/colorSheme";
import {ICON_NAMES, SessionStorageManager} from "../../../../../utilities/generalConstants.js/generalConstants";
import {selectCurrentCof} from "../../KpFinSlice";

export const KP_FIN_TABLE_STORAGE_KEY = "kpFinTable";

const KP_FIN_IMAGE_COLUMN_SIZE = 200;
const KP_FIN_IMAGE_COLUMN_MIN_SIZE = 200;

const useKpFinCollumnsService = (toggledColl, handleColumnDragStart, handleColumnDrop, initialSettings, kpReadOnly) => {
  const {t} = useTranslation();
  const currentCOf = useSelector(selectCurrentCof);
  const managerSettings = currentCOf?.co?.managerCo?.settings;

  const normalizeSettingsValue = value => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string") {
      return value
        .split(",")
        .map(item => item?.trim())
        .filter(Boolean);
    }
    return [];
  };

  const hasInitialHiddenColumns = Object.prototype.hasOwnProperty.call(initialSettings || {}, "hiddenColumns");
  const hiddenColumnsFromInitial = normalizeSettingsValue(initialSettings?.hiddenColumns);
  const hiddenColumnsFromCurrentCof = normalizeSettingsValue(currentCOf?.hiddenColumns);
  const hiddenColumnsFromManager = normalizeSettingsValue(managerSettings?.hiddenColumns);

  const hiddenColumns = hasInitialHiddenColumns
    ? hiddenColumnsFromInitial
    : hiddenColumnsFromCurrentCof?.length > 0
    ? hiddenColumnsFromCurrentCof
    : hiddenColumnsFromManager;

  const findIfDisabled = accessorKey => (hiddenColumns ? hiddenColumns?.includes(accessorKey) : false);

  const kpFinCollumns = useMemo(() => {
    const imgcellRender = cell => {
      const imgs = cell.getValue();
      if (imgs) {
        return (
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%"}}>
            <div key={imgs} style={{position: "relative", width: "100%"}}>
              <img
                src={apiUrl + "/" + imgs}
                alt={imgs}
                style={{width: "100%", height: "auto", objectFit: "contain", marginRight: "2px"}}
              />
            </div>
          </div>
        );
      }
    };
    return [
      {
        header: () => "№",
        headerName: "№",
        accessorKey: "number",
        size: 15,
      },
      {
        header: () => [t("SKU")],
        headerName: t("SKU"),
        accessorKey: "article",
        size: 100,
      },
      {
        header: () => [t("Brand w*")],
        headerName: t("Brand w*"),
        accessorKey: "brandName",
        cell: value => (
          <span
            style={value.row.original.brandBlackList ? {color: COLOR_SCHEME.ALERT} : undefined}
            title={value.row.original.brandBlackList ? t("Factory is blocked") : ""}
          >
            {value.getValue()}
          </span>
        ),
      },
      {
        header: () => [t("Nomenclature")],
        headerName: t("Nomenclature"),
        accessorKey: "skuName",
      },
      {
        header: () => [t("Characteristics")],
        headerName: t("Characteristics"),
        accessorKey: "characteristics",
        size: 200,
      },
      {
        header: () => [t("VWC")],
        headerName: t("VWC"),
        accessorKey: "ovh",
        minSize: 70,
        maxSize: 180,
        size: 100,
      },
      {
        header: () => [t("Image")],
        headerName: t("Image"),
        accessorKey: "image1",
        // minSize: 90,
        // size: 140,
        size: KP_FIN_IMAGE_COLUMN_SIZE,
        minSize: KP_FIN_IMAGE_COLUMN_MIN_SIZE,
        maxSize: 260,
        cell: imgcellRender,
      },
      {
        header: () => [t("Image")],
        headerName: t("Image"),
        accessorKey: "image2",
        // minSize: 90,
        // size: 140,
        size: KP_FIN_IMAGE_COLUMN_SIZE,
        minSize: KP_FIN_IMAGE_COLUMN_MIN_SIZE,
        maxSize: 260,
        cell: imgcellRender,
        notDraggable: true,
      },
      {
        header: () => [t("Set description")],
        headerName: t("Set description"),
        accessorKey: "complectDesc",
      },
      {
        header: () => [t("Measur units")],
        headerName: t("Measur units"),
        accessorKey: "measurUnits",
        size: 70,
      },
      {
        header: () => [t("Price (€)")],
        headerName: t("Price (€)"),
        accessorKey: "price",
        size: 100,
        cell: value => (
          <div
            style={{
              color: value.row.original.isPriceOutRange ? COLOR_SCHEME.ALERT_BRIGHT : "",
              textAlign: "center",
            }}
          >
            {value.getValue()}
          </div>
        ),
      },
      {
        header: () => [t("Discount %")],
        headerName: t("Discount %"),
        accessorKey: "discount",
        size: 90,
      },
      {
        header: () => [t("Sum € ")],
        headerName: t("Sum € "),
        accessorKey: "summ",
        size: 100,
        notDraggable: true,
      },
      {
        header: () => [t("Total discount %")],
        headerName: t("Total discount %"),
        accessorKey: "topDiscount",
        size: 90,
      },
      {
        header: () => [t("Total € ")],
        headerName: t("Total € "),
        accessorKey: "totalSumm",
        notDraggable: true,
      },
      {
        header: () => [t("Quantity")],
        headerName: t("Quantity"),
        accessorKey: "amount",
        size: 80,
        cell: value => (
          <div
            style={{
              color: value.row.original.isAmountFractional ? COLOR_SCHEME.ALERT_BRIGHT : "",
              textAlign: "center",
            }}
          >
            {value.getValue()}
          </div>
        ),
      },
      {
        header: () => [t("Payment €")],
        headerName: t("Payment €"),
        accessorKey: "paymentSumm",
        numberFormat: true,
      },
    ];
  }, [t]);

  const collumns = useMemo(() => {
    const kpFinTableStorageManager = new SessionStorageManager(KP_FIN_TABLE_STORAGE_KEY);
    const hasInitialColumnsPosition = Object.prototype.hasOwnProperty.call(initialSettings || {}, "columnsPosition");

    const columnsPositionFromInitial = normalizeSettingsValue(initialSettings?.columnsPosition);
    const columnsPositionFromCurrentCof = normalizeSettingsValue(currentCOf?.columnsPosition);
    const columnsPositionFromManager = normalizeSettingsValue(managerSettings?.columnsPosition);
    const columnsPositionFromStorage = normalizeSettingsValue(kpFinTableStorageManager.getItem());

    const storedCollKeys = hasInitialColumnsPosition
      ? columnsPositionFromInitial
      : columnsPositionFromCurrentCof.length > 0
      ? columnsPositionFromCurrentCof
      : columnsPositionFromManager.length > 0
      ? columnsPositionFromManager
      : columnsPositionFromStorage;

    const orderedKeysFromSettings = Array.isArray(storedCollKeys)
      ? storedCollKeys.filter(
          accessorKey => accessorKey !== "number" && kpFinCollumns.some(col => col.accessorKey === accessorKey),
        )
      : [];

    const missingKeys = kpFinCollumns
      .map(({accessorKey}) => accessorKey)
      .filter(accessorKey => accessorKey !== "number" && !orderedKeysFromSettings.includes(accessorKey));

    const fullOrderedKeys = ["number", ...orderedKeysFromSettings, ...missingKeys];

    const kpFinCollsInStoredOrder = fullOrderedKeys.map(accessorKey =>
      kpFinCollumns.find(col => col.accessorKey === accessorKey),
    );

    const kpFinColsWithDragNDrop = kpFinCollsInStoredOrder.map(col =>
      !kpReadOnly && col?.accessorKey !== "number"
        ? {
            ...col,
            header: ({column} = {}) => {
              return (
                <div
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    // if (column.columnDef?.notDraggable) return;
                    handleColumnDrop(e, column.id);
                  }}
                >
                  <div
                    onDragStart={e => {
                      if (column.columnDef?.notDraggable) return;
                      handleColumnDragStart(e, column.id);
                    }}
                    draggable={!column.columnDef?.notDraggable}
                    className="row aic gap5"
                  >
                    <IconButton
                      nameIcon={column.columnDef?.notDraggable ? ICON_NAMES.LINK : ICON_NAMES.HAND}
                      tooltipMessage={
                        column.columnDef?.notDraggable
                          ? "Paired columns can`t be moved from each other!"
                          : "change columns order"
                      }
                      widthIcon="0.7rem"
                      disabled={column.columnDef?.notDraggable}
                    />
                    <span>{col.headerName || "sdfsdf"}</span>
                  </div>
                </div>
              );
            },
          }
        : col,
    );
    if (toggledColl?.length > 0) {
      const withDisabledColls = kpFinColsWithDragNDrop.map(coll => {
        if (toggledColl.includes(coll.accessorKey))
          return {
            ...coll,
            toggled: true,
            isDisabled: findIfDisabled(coll.accessorKey),
          };
        return coll;
      });
      return withDisabledColls;
    } else {
      return kpFinColsWithDragNDrop;
    }
  }, [
    currentCOf?.cofId,
    currentCOf?.hiddenColumns,
    currentCOf?.columnsPosition,
    currentCOf?.co?.managerCo?.settings,
    kpFinCollumns,
    initialSettings,
  ]);

  return {collumns};
};

export default useKpFinCollumnsService;
